import type { CartesianPose } from '@sb/geometry';
import { convertEulerPose, ZERO_POSE } from '@sb/geometry';

import type { LocateMethod, ResultOrdering } from '../../vision/LocateTypes';
import {
  BlobDetection2DMethod,
  CustomCheckpointDetection2DMethod,
  DinoFastMethod,
  ShapeDetection2DMethod,
  TextPromptDetection2DMethod,
} from '../../vision/LocateTypes';
import {
  DEFAULT_RANSAC_MAX_ITERATIONS,
  DEFAULT_RANSAC_MIN_SAMPLES,
} from '../Locate/Arguments';

/**
 * Segmentation settings stored on the skill's `shared.matches` (MatchesConfig
 * in `@sb/ai`) rather than in `methodConfig`. Structural type so routine-runner
 * doesn't need to depend on the `@sb/ai` schema.
 */
export interface MatchesSegmentationConfig {
  enableSegmentationMask: boolean;
  segmentationMethod: 'sam2' | 'bilateral_solver' | 'heatmap_threshold';
}

/**
 * Converts a saved Locate skill's method name and raw methodConfig into the
 * LocateMethod discriminated union that runLocate consumes.
 *
 * Each branch delegates validation to the corresponding routine-runner Zod
 * schema so that any mismatched field is caught at call-time with a descriptive
 * parse error rather than silently producing wrong values.
 *
 * `matches` carries the segmentation settings the skill stores on
 * `shared.matches` (not in `methodConfig`). They only apply to DinoFast —
 * without them `DinoFastParams` falls back to its schema defaults
 * (mask on, bilateral solver), silently ignoring what was saved on the skill.
 */
export function mapSkillMethodToLocateMethod(
  method: string,
  methodConfig: Record<string, unknown>,
  matches?: MatchesSegmentationConfig,
): LocateMethod {
  switch (method) {
    case 'dino_fast':
      return DinoFastMethod.parse({
        kind: 'DinoFast',
        settings: matches
          ? {
              ...methodConfig,
              enableSegmentationMask: matches.enableSegmentationMask,
              segmentationMethod: matches.segmentationMethod,
            }
          : methodConfig,
      });

    case 'blob':
      return BlobDetection2DMethod.parse({
        kind: 'BlobDetection2D',
        settings: methodConfig,
      });

    case 'shape_match':
      // The wizard saves a FLAT `ShapeMatchSkillConfig` (@sb/ai/types): the
      // Shape2DParams fields (minScale, maxScale, matchThreshold, …) plus a
      // peer `templateImage` key (`{ storageID }`). Lift `templateImage` out as
      // a sibling of `settings` to match the runtime `ShapeDetection2DMethod`.
      return ShapeDetection2DMethod.parse({
        kind: 'ShapeDetection2D',
        templateImage: methodConfig.templateImage,
        settings: methodConfig,
      });

    case 'text_prompt':
      return TextPromptDetection2DMethod.parse({
        kind: 'TextPromptDetection2D',
        settings: methodConfig,
      });

    case 'custom_checkpoint':
      return CustomCheckpointDetection2DMethod.parse({
        kind: 'CustomCheckpointDetection2D',
        settings: methodConfig,
      });

    default:
      throw new Error(
        `RunLocateSkill: unsupported locate method '${method}'. Supported: dino_fast, blob, shape_match, text_prompt, custom_checkpoint.`,
      );
  }
}

/**
 * Converts a saved Locate skill's projection transform into the `CartesianPose`
 * that runLocate's `transform` arg expects.
 *
 * The skill stores translation (`x`/`y`/`z`) in MILLIMETERS while runLocate (and
 * the rest of the geometry stack) works in METERS, so we scale by 1e-3. Rotation
 * (`roll`/`pitch`/`yaw`) is in degrees, which `convertEulerPose.toCartesian`
 * already expects, so it passes through unscaled.
 *
 * Note: the transform's `graspFrame` is intentionally NOT consumed here — it is
 * a reference *frame* (object / plane / world), not part of the offset pose.
 * `RunLocateSkill/Step.ts` reads it and forwards it to runLocate as
 * `graspFrame` alongside this offset.
 */
export function skillTransformToCartesian(
  transform: {
    x: number;
    y: number;
    z: number;
    roll: number;
    pitch: number;
    yaw: number;
  } | null,
): CartesianPose {
  if (!transform) {
    return ZERO_POSE;
  }

  return convertEulerPose.toCartesian({
    // Skill stores translation in mm; geometry stack uses meters.
    X: transform.x * 0.001,
    Y: transform.y * 0.001,
    Z: transform.z * 0.001,
    Rx: transform.roll,
    Ry: transform.pitch,
    Rz: transform.yaw,
  });
}

/**
 * The RANSAC fit parameters behind each operator-facing strictness preset.
 *
 * A depth-surface skill stores only the preset name (`DepthSurfaceConfig` in
 * `@sb/ai/types`), so this table is the single source of truth for the numbers:
 * the runtime expands it into locate arguments below, and the wizard reads it to
 * caption what the selected preset actually does.
 *
 * Every tilt stays inside [1, 90] and every ratio inside [0, 1], so no preset
 * can reach the backend as the 0 "disabled" tilt sentinel or an out-of-range
 * inlier floor. Tilts are far more permissive than the shared 15-degree default
 * of the plane path — fitting a surface per detection exists to pick tilted
 * parts, which 15 would reject.
 */
export const DEPTH_SURFACE_STRICTNESS_PRESETS = {
  permissive: {
    inlierThresholdMm: 8,
    maxTiltDeg: 60,
    minInlierRatio: 0.3,
  },
  balanced: {
    inlierThresholdMm: 5,
    maxTiltDeg: 45,
    minInlierRatio: 0.5,
  },
  strict: {
    inlierThresholdMm: 3,
    maxTiltDeg: 30,
    minInlierRatio: 0.7,
  },
} as const;

export type DepthSurfaceStrictness =
  keyof typeof DEPTH_SURFACE_STRICTNESS_PRESETS;

export const DEFAULT_DEPTH_SURFACE_STRICTNESS: DepthSurfaceStrictness =
  'balanced';

/**
 * What a match whose surface was rejected falls back to, when the skill doesn't
 * say. On: a top-down world-frame grasp. This is deliberately the permissive
 * default — a skill saved before the flag became its own control, or one written
 * by hand, keeps returning every match rather than silently dropping the ones
 * the fit couldn't measure.
 */
export const DEFAULT_DEPTH_SURFACE_FALLBACK_TO_TOP_DOWN = true;

/**
 * Converts a saved Locate skill's surface-fit config into the locate arguments
 * that drive the per-detection fit. This is the single boundary where the
 * preset's operator-facing units (mm, degrees) become the runtime's metres,
 * mirroring `skillTransformToCartesian`.
 *
 * Resolved for `DetectedDepth` as well as `DetectedPlane`, even though nothing
 * is fitted there: the arguments go nowhere on the wire (the DetectedDepth arm
 * carries no parameters), and keeping them resolved means flipping the
 * operator's "Detect surface tilt" switch back on restores their own preset
 * rather than a default.
 *
 * A missing or unrecognised strictness resolves to the balanced preset, so a
 * skill saved without one still fits surfaces the same way the wizard's default
 * does. The top-down fallback is a separate saved field rather than part of the
 * preset: strictness decides what counts as a good surface, and this decides
 * what happens to the matches that fail it.
 */
export function depthSurfaceLocateArgs(
  config:
    | { strictness?: string; fallbackToTopDown?: boolean }
    | null
    | undefined,
): {
  ransacParams: {
    maxIterations: number;
    distanceThreshold: number;
    minSamples: number;
  };
  maxTiltAngle: number;
  minInlierRatio: number;
  fallbackToTopDown: boolean;
} {
  const preset =
    DEPTH_SURFACE_STRICTNESS_PRESETS[
      config?.strictness as DepthSurfaceStrictness
    ] ?? DEPTH_SURFACE_STRICTNESS_PRESETS[DEFAULT_DEPTH_SURFACE_STRICTNESS];

  return {
    ransacParams: {
      maxIterations: DEFAULT_RANSAC_MAX_ITERATIONS,
      // Presets are stated in mm; the fit works in meters.
      distanceThreshold: preset.inlierThresholdMm * 0.001,
      minSamples: DEFAULT_RANSAC_MIN_SAMPLES,
    },
    maxTiltAngle: preset.maxTiltDeg,
    minInlierRatio: preset.minInlierRatio,
    fallbackToTopDown:
      config?.fallbackToTopDown ?? DEFAULT_DEPTH_SURFACE_FALLBACK_TO_TOP_DOWN,
  };
}

/**
 * Converts a saved Locate skill's MatchesConfig into the filter arguments that
 * runLocate accepts.
 */
export function mapMatchesToFilters(matches: {
  limitResults: number;
  orderBy: ResultOrdering;
}): { resultsLimit: number; resultOrdering: ResultOrdering } {
  return {
    resultsLimit: matches.limitResults,
    resultOrdering: matches.orderBy,
  };
}
